import { createHttpClient } from '@adapters/HttpClient';
import { CourierInvoicesApiAdapter } from '../../adapters';
import type { ImportCourierInvoiceResponse } from '../../models/types';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useToast } from '@shared/hooks/useToast';
import { useTranslation } from 'react-i18next';

export const useImportCourierInvoiceFile = (invoiceId: number) => {
  const { toast } = useToast();
  const { t } = useTranslation('courierInvoicesModule');
  const queryClient = useQueryClient();
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new CourierInvoicesApiAdapter(httpClient), [httpClient]);

  const mutation = useMutation({
    mutationFn: (payload: {
      file: File;
      period_start?: string;
      period_end?: string;
    }): Promise<ImportCourierInvoiceResponse> => adapter.importFile(invoiceId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['courier-invoices-infinite'] });
      queryClient.invalidateQueries({ queryKey: ['courier-invoice-detail', invoiceId] });
      toast({
        title: t('import.success_title'),
        description: data.message ?? t('import.success_description', { id: data.courier_settlement_id }),
      });
    },
    onError: (error: { response?: { status?: number; data?: { message?: string } }; message?: string }) => {
      const msg = error?.response?.data?.message ?? error?.message ?? t('errors.unexpected');
      const is409 = error?.response?.status === 409;
      toast({
        title: is409 ? t('import.error_duplicate_title') : t('import.error_title'),
        description: msg,
        variant: 'destructive',
      });
    },
  });

  return {
    importFileAsync: mutation.mutateAsync,
    isImporting: mutation.isPending,
  };
};
